import type { LabeledStat } from '@/components/common/StatCardGrid';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { formatNumber } from '@/utils/number';
import type {
  BillingApprovalTotals,
  BillingClaimTotals,
  BillingLimits,
} from '../types/billing.types';

const CLAIM_STAT_LABELS = {
  dcr: { count: 'Entries', claimed: 'Claimed Fare' },
  der: { count: 'Entries', claimed: 'Claimed Amount' },
  expense: { count: 'Expenses', claimed: 'Claimed' },
  dcrApprove: { count: 'DCRs', claimed: 'Claimed Fare' },
} as const;

const APPROVED_STAT_LABELS = {
  expense: 'Approved',
  dcrApprove: 'Approved Fare',
} as const;

const AMOUNT_LIMIT_LABELS = {
  dcr: 'Max Fare',
  der: 'Max Amount',
} as const;

type ClaimStatKind = keyof typeof CLAIM_STAT_LABELS;
type ApprovalStatKind = keyof typeof APPROVED_STAT_LABELS;
type LimitStatKind = keyof typeof AMOUNT_LIMIT_LABELS;

interface BillingListMeta<TTotals> {
  total: number;
  totals: TTotals;
}

export function getClaimStatCards(
  kind: ClaimStatKind,
  meta: BillingListMeta<BillingClaimTotals> | undefined,
): LabeledStat[] {
  const labels = CLAIM_STAT_LABELS[kind];

  return [
    { key: 'count', label: labels.count, value: meta?.total },
    { key: 'members', label: 'ZMs', value: meta?.totals.members },
    {
      key: 'claimed',
      label: labels.claimed,
      value: meta?.totals.claimed,
      unit: TAKA_SYMBOL,
    },
  ];
}

export function getLimitStatCards(
  kind: LimitStatKind,
  limits: BillingLimits | undefined,
): LabeledStat[] {
  const amountRange = kind === 'dcr' ? limits?.dcrFare : limits?.derFare;
  const cards: LabeledStat[] = [
    {
      key: 'amountLimit',
      label: AMOUNT_LIMIT_LABELS[kind],
      value: amountRange?.high,
      unit: TAKA_SYMBOL,
      hint: amountRange
        ? `Approvable from ${formatNumber(amountRange.low)} to ${formatNumber(amountRange.high)} Tk`
        : undefined,
    },
    {
      key: 'dayLimit',
      label: 'Verify Within',
      value: limits?.approveDayHigh,
      unit: 'days',
    },
  ];

  if (kind === 'dcr') {
    cards.push({
      key: 'specimenLimit',
      label: 'Specimens At Once',
      value: limits?.specimenQtyHigh,
      hint: limits
        ? `Up to ${formatNumber(limits.specimenQtyHigh)} specimens in one entry`
        : undefined,
    });
  }

  return cards;
}

export function getApprovalStatCards(
  kind: ApprovalStatKind,
  meta: BillingListMeta<BillingApprovalTotals> | undefined,
): LabeledStat[] {
  return [
    ...getClaimStatCards(kind, meta),
    {
      key: 'approved',
      label: APPROVED_STAT_LABELS[kind],
      value: meta?.totals.approved,
      unit: TAKA_SYMBOL,
    },
  ];
}
